import React from 'react';
import {Divider, IconButton, Stack, Typography} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import SourcePackage from './SourcePackage';
import useSourcePackageBuilderStore from '../../stores/useSourcePackageBuilderStore';
import SegmentItem from '../segmentItem';
import {unifiedHitToSegmentProps} from '../../features/products/reportEditorPage/leftPanel/components/unifiedHitToSegmentProps';
import {useEffectiveQuery} from '../documentContent/useEffectiveQuery';

const SIDE_PANEL_WIDTH = '20%';

/**
 * SourcePackageSidePanel - Side panel displaying the source package contents
 * with a header and close button. When a federated search hit is open, the selected
 * segment is surfaced as a rich card pinned at the top so the right side always shows
 * what the user clicked.
 *
 * @param {Object} props
 * @param {Function} props.onClose - Callback to close the panel
 */
const SourcePackageSidePanel = ({onClose}) => {
    const selectedHit = useSourcePackageBuilderStore((s) => s.selectedHit);
    const query = useEffectiveQuery();
    const selectedSegmentProps = React.useMemo(
        () => (selectedHit ? unifiedHitToSegmentProps(selectedHit, {query}) : null),
        [selectedHit, query],
    );

    return (
        <Stack
            sx={{
                width: SIDE_PANEL_WIDTH,
                borderRadius: 2,
                flex: 1,
                minHeight: 0,
                overflow: 'hidden',
                bgcolor: 'background.paper',
            }}
        >
            <Stack
                px={2}
                py={1}
                pr={1}
                justifyContent="space-between"
                alignItems="center"
                direction="row"
                flexShrink={0}
            >
                <Typography variant="h6">Source Package</Typography>
                <IconButton onClick={onClose} aria-label="Close source package builder">
                    <CloseRoundedIcon/>
                </IconButton>
            </Stack>

            <Divider/>

            <Stack flex={1} minHeight={0} overflow="auto">
                {selectedSegmentProps && (
                    <Stack sx={{p: 1.5, flexShrink: 0}} spacing={1}>
                        <Typography variant="overline" color="text.secondary">
                            Selected segment
                        </Typography>
                        <SegmentItem props={selectedSegmentProps}/>
                        <Divider/>
                    </Stack>
                )}

                <SourcePackage embeddedInSidePanel/>
            </Stack>
        </Stack>
    );
};

export default React.memo(SourcePackageSidePanel);

